// Get help: the company's side of the service desk. Search help articles, report a problem or ask for something,
// follow your own tickets and reply on them, and approve what your team asks for.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { Icon } from "../icons";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import { sd, REQUESTER_STATE, when, type Article, type RequestType } from "./sdApi";

function ArticleHits({ q }: { q: string }) {
  const [hits, setHits] = useState<Article[]>([]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) { setHits([]); return; }
    const t = setTimeout(() => sd.articles(term).then(setHits).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!hits.length) return null;
  return (
    <div className="sd-hits" role="region" aria-label="Help articles that might answer this">
      <div className="label">These might help</div>
      {hits.map((a) => <Link key={a.id} to={`/help/kb/${a.id}`} className="sd-hit"><span className="t">{a.title}</span><span className="s">{a.summary}</span></Link>)}
    </div>
  );
}

export function HelpHome() {
  const me = useLoad(() => sd.me());
  const types = useLoad(() => sd.types());
  const mine = useLoad(() => sd.tickets("requested"));
  const [q, setQ] = useState("");
  const shown = (kind: string) => (types.data ?? []).filter((t) => t.active && t.kind === kind && (!q.trim() || `${t.name} ${t.description}`.toLowerCase().includes(q.trim().toLowerCase())));
  const openMine = (mine.data ?? []).filter((t) => !["closed", "cancelled"].includes(t.state));
  return (
    <>
      <CommandBar status={me.data?.role === "provider" ? "You're signed in as provider staff" : undefined}>
        {me.data?.role !== "requester" && <Link className="cmd" to="/service-desk">Service desk</Link>}
        <Link className="cmd" to="/service-desk/approvals">Approvals{me.data?.approvalsPending ? ` (${me.data.approvalsPending})` : ""}</Link>
      </CommandBar>
      <div className="page sd-help">
        <div className="sd-hero">
          <h1>How can we help?</h1>
          <label className="apps-find sd-search"><Icon name="search" size={16} />
            <input type="search" aria-label="Search help and request types" placeholder="Describe the problem, or search help articles" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={window.innerWidth > 700} />
          </label>
          <ArticleHits q={q} />
        </div>
        <ErrorBox error={types.error ?? mine.error} />
        {me.data?.approvalsPending ? <Link to="/service-desk/approvals" className="msg info sd-banner">{me.data.approvalsPending} request{me.data.approvalsPending > 1 ? "s are" : " is"} waiting for your approval.</Link> : null}
        <div className="sd-typegroups">
          {[["incident", "Something's wrong"], ["request", "Ask for something"]].map(([kind, title]) => (
            <section key={kind}>
              <h2 className="sd-h2">{title}</h2>
              <div className="sd-types">
                {shown(kind).map((t) => (
                  <Link key={t.id} to={`/help/new/${t.id}`} className="sd-type">
                    <span className="n">{t.name}</span><span className="d">{t.description}</span>
                    {t.approval !== "none" && <span className="a">Needs approval</span>}
                  </Link>
                ))}
                {types.data && !shown(kind).length && <div className="muted">Nothing matches.</div>}
              </div>
            </section>
          ))}
        </div>
        <section className="card" style={{ marginTop: 20 }}>
          <div className="card-head"><h2>Your tickets</h2><span className="muted">{openMine.length} open</span></div>
          {!mine.data ? <div className="empty">Loading…</div> : !mine.data.length ? <div className="empty">You haven't raised anything yet.</div> : (
            <div className="table-wrap"><table className="dgrid">
              <thead><tr><th>Number</th><th>What</th><th>Status</th><th>Raised</th><th>Last update</th></tr></thead>
              <tbody>{mine.data.map((t) => (
                <tr key={t.id}>
                  <td><Link className="num" to={`/help/${t.number}`}>{t.number}</Link></td>
                  <td className="title">{t.title}</td>
                  <td><span className={`sd-state s-${t.state}`}>{REQUESTER_STATE[t.state] ?? t.state}</span></td>
                  <td className="muted">{when(t.createdAt)}</td><td className="muted">{when(t.updatedAt)}</td>
                </tr>))}
              </tbody>
            </table></div>
          )}
        </section>
      </div>
    </>
  );
}

const IMPACT = [{ v: 3, l: "Just me" }, { v: 2, l: "My team or department" }, { v: 1, l: "The whole company, or customers" }];
const URGENCY = [{ v: 3, l: "Yes, it can wait" }, { v: 2, l: "Yes, but slowly" }, { v: 1, l: "No, work has stopped" }];

export function HelpNew() {
  const { typeId = "" } = useParams();
  const nav = useNavigate();
  const types = useLoad(() => sd.types());
  const rt: RequestType | undefined = types.data?.find((t) => t.id === typeId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [impact, setImpact] = useState(3);
  const [urgency, setUrgency] = useState(3);
  const [approver, setApprover] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const t = await sd.create({ requestTypeId: typeId, title: title || undefined, description, answers, impact, urgency: rt?.kind === "request" ? 2 : urgency, approverEmail: approver, source: "portal" });
      nav(`/help/${t.number}`);
    } catch (x) { setErr(x); setBusy(false); }
  };
  if (types.data && !rt) return <div className="page"><div className="card empty">That request type isn't available. <Link to="/help">Back to Get help</Link></div></div>;
  return (
    <>
      <CommandBar><Link className="cmd" to="/help">Get help</Link></CommandBar>
      <div className="page sd-form">
        <div className="page-title"><h1>{rt?.name ?? "…"}</h1><span className="sub">{rt?.description}</span></div>
        <form onSubmit={submit} className="card" style={{ padding: 20 }}>
          <div className="field"><label htmlFor="sd-title">{rt?.kind === "request" ? "A short summary" : "What's wrong, in a few words"}</label>
            <input id="sd-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={rt?.kind === "request" ? rt?.name : "For example: can't print to the warehouse printer"} maxLength={200} required={rt?.kind !== "request"} />
          </div>
          {rt?.kind === "incident" && <ArticleHits q={title} />}
          {rt?.fields.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={`f-${f.key}`}>{f.label}{f.required ? "" : <span className="muted"> (optional)</span>}</label>
              {f.type === "textarea" ? <textarea id={`f-${f.key}`} className="textarea" rows={3} required={f.required} value={answers[f.key] ?? ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} />
                : f.type === "select" ? <select id={`f-${f.key}`} className="select" required={f.required} value={answers[f.key] ?? ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })}><option value="">Choose…</option>{(f.options ?? []).map((o) => <option key={o}>{o}</option>)}</select>
                : <input id={`f-${f.key}`} className="input" type={f.type === "date" ? "date" : "text"} required={f.required} value={answers[f.key] ?? ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} />}
            </div>
          ))}
          <div className="field"><label htmlFor="sd-desc">{rt?.kind === "request" ? "Anything else we should know" : "What happened? What did you expect?"}</label>
            <textarea id="sd-desc" className="textarea" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {rt?.kind === "incident" && (
            <div className="sd-two">
              <fieldset className="sd-choice"><legend>Who does this affect?</legend>
                {IMPACT.map((o) => <label key={o.v}><input type="radio" name="impact" checked={impact === o.v} onChange={() => setImpact(o.v)} />{o.l}</label>)}</fieldset>
              <fieldset className="sd-choice"><legend>Can you still work?</legend>
                {URGENCY.map((o) => <label key={o.v}><input type="radio" name="urgency" checked={urgency === o.v} onChange={() => setUrgency(o.v)} />{o.l}</label>)}</fieldset>
            </div>
          )}
          {rt?.approval === "manager" && (
            <div className="field"><label htmlFor="sd-appr">Your manager's email</label>
              <input id="sd-appr" className="input" type="email" required value={approver} onChange={(e) => setApprover(e.target.value)} placeholder="name@company.com" />
              <span className="hint">They approve it before IT starts. They'll get an email.</span>
            </div>
          )}
          {rt?.approval === "fixed" && <p className="muted">{rt.approverEmail} approves this before IT starts.</p>}
          <ErrorBox error={err} />
          <div className="btn-row"><button className="btn primary" disabled={busy}>{busy ? "Sending…" : "Send"}</button><Link className="btn" to="/help">Cancel</Link></div>
        </form>
      </div>
    </>
  );
}

export function HelpTicket() {
  const { number = "" } = useParams();
  const [tick, setTick] = useState(0);
  const t = useLoad(() => sd.ticket(number), [number, tick]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const send = async (e: FormEvent) => {
    e.preventDefault(); if (!reply.trim()) return;
    setBusy(true); setErr(null);
    try { await sd.comment(number, { body: reply, visibility: "public" }); setReply(""); setTick((x) => x + 1); } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const d = t.data;
  const steps = useMemo(() => ["Received", "Being worked on", "Resolved"], []);
  const at = !d ? 0 : ["resolved", "closed"].includes(d.state) ? 2 : ["new", "awaiting_approval"].includes(d.state) ? 0 : 1;
  return (
    <>
      <CommandBar><Link className="cmd" to="/help">Get help</Link>{d && d.role !== "requester" && <Link className="cmd" to={`/service-desk/${d.number}`}>Open in the service desk</Link>}</CommandBar>
      <div className="page sd-form">
        <ErrorBox error={t.error} />
        {d && (
          <>
            <div className="page-title"><h1>{d.title}</h1><span className="sub">{d.number} · {REQUESTER_STATE[d.state]}</span></div>
            <ol className="sd-progress">{steps.map((s, i) => <li key={s} className={i < at ? "done" : i === at ? "now" : ""}>{s}</li>)}</ol>
            {d.state === "waiting_requester" && <div className="msg warn">The service desk is waiting for your reply.</div>}
            {d.state === "awaiting_approval" && <div className="msg info">Waiting for {d.approvals.find((a) => !a.decision)?.approverEmail} to approve.</div>}
            <section className="card" style={{ padding: 16 }}>
              {d.description && <p style={{ whiteSpace: "pre-wrap", marginTop: 0 }}>{d.description}</p>}
              {Object.keys(d.answers).length > 0 && <dl className="dl">{Object.entries(d.answers).flatMap(([k, v]) => [<dt key={`k${k}`}>{k}</dt>, <dd key={`v${k}`}>{v}</dd>])}</dl>}
            </section>
            <section className="sd-thread" aria-label="Conversation">
              {d.comments.map((c) => (
                <div key={c.id} className={`sd-msg ${c.authorId === d.requesterId ? "me" : ""} ${c.role === "system" ? "sys" : ""}`}>
                  <div className="h"><b>{c.role === "system" ? "Update" : c.authorName}</b>{c.role === "provider" && <span className="muted"> · IT provider</span>}{c.role === "agent" && <span className="muted"> · Service desk</span>}<span className="muted"> · {when(c.at)}</span></div>
                  <div className="b">{c.body}</div>
                </div>
              ))}
              {!d.comments.length && <p className="muted">No replies yet.</p>}
            </section>
            {!["closed", "cancelled"].includes(d.state) && (
              <form onSubmit={send} className="card" style={{ padding: 16 }}>
                <label className="label" htmlFor="sd-reply">{d.state === "resolved" ? "Not fixed? Reply and it reopens" : "Reply"}</label>
                <textarea id="sd-reply" className="textarea" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} style={{ marginTop: 6 }} />
                <ErrorBox error={err} />
                <div className="btn-row" style={{ marginTop: 8 }}><button className="btn primary" disabled={busy || !reply.trim()}>Send reply</button></div>
              </form>
            )}
          </>
        )}
      </div>
    </>
  );
}

export function HelpArticle() {
  const { id = "" } = useParams();
  const a = useLoad(() => sd.article(id), [id]);
  const [thanks, setThanks] = useState(false);
  return (
    <>
      <CommandBar><Link className="cmd" to="/help">Get help</Link></CommandBar>
      <div className="page sd-form">
        <ErrorBox error={a.error} />
        {a.data && (
          <article className="card" style={{ padding: 20 }}>
            <h1 style={{ marginTop: 0, fontSize: 20 }}>{a.data.title}</h1>
            <div className="sd-article">{a.data.body}</div>
            <div className="btn-row" style={{ marginTop: 16 }}>
              {thanks ? <span className="muted">Thanks, that helps us know what works.</span> : <button className="btn" onClick={() => sd.helped(id).then(() => setThanks(true))}>This fixed it</button>}
              <Link className="btn" to="/help">I still need help</Link>
            </div>
          </article>
        )}
      </div>
    </>
  );
}
